import { extractHeadings } from '@slate/core';

/** Max length for a heading-derived filename stem (without extension). Long
 *  enough to keep most titles intact, short enough to survive every filesystem
 *  and look reasonable in the Save dialog's path field. */
const MAX_STEM_LENGTH = 80;

/** Names Windows reserves no matter the extension. We don't refuse them — we
 *  prefix with `_` so the user still gets a sensible suggestion. */
const RESERVED_WINDOWS_NAMES = new Set([
  'CON', 'PRN', 'AUX', 'NUL',
  'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
  'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9',
]);

/**
 * Turn a markdown heading's plain-text into a cross-platform-safe filename
 * stem (no extension). Returns `null` when the input is empty or sanitizes
 * down to nothing — callers fall back to a generic name.
 *
 * Rules:
 *   - strip control chars and OS-reserved chars (`/ \ : * ? " < > |` + NUL)
 *   - collapse runs of whitespace into single spaces
 *   - trim leading/trailing whitespace AND dots (Windows refuses trailing `.`)
 *   - cap at MAX_STEM_LENGTH characters
 *   - prefix `_` when the result matches a Windows reserved device name
 */
export function safeFilenameStem(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  // Whitespace pass MUST run before the control-strip — otherwise tabs/
  // newlines get dropped as control chars and adjacent words fuse together.
  let s = raw
    .replace(/\s+/g, ' ')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f<>:"/\\|?*\x7f]/g, '')
    .trim()
    .replace(/^\.+|\.+$/g, '')
    .trim();
  if (!s) return null;
  if (s.length > MAX_STEM_LENGTH) s = s.slice(0, MAX_STEM_LENGTH).trimEnd();
  if (RESERVED_WINDOWS_NAMES.has(s.toUpperCase())) s = `_${s}`;
  return s;
}

/**
 * Derive a default save filename from a document's content and target language.
 * For markdown (`primaryExt === 'md'`) the stem comes from the first heading so
 * a doc that starts with `# Project notes` lands in the dialog as
 * `Project notes.md`. For every other language the stem is generic (`untitled`)
 * since code/text files have no heading convention. Falls back to `untitled`
 * whenever no usable heading is found.
 *
 * @param text the document text (markdown source when `primaryExt === 'md'`)
 * @param primaryExt the target extension (no dot), default `md`
 */
export function defaultFilenameForDoc(text: string, primaryExt = 'md'): string {
  const ext = primaryExt || 'md';
  if (ext === 'md') {
    const headings = extractHeadings(text);
    const first = headings[0];
    const stem = first ? safeFilenameStem(first.text) : null;
    return `${stem ?? 'untitled'}.${ext}`;
  }
  return `untitled.${ext}`;
}
